// Componenti UI riutilizzabili

import { Loader2 } from 'lucide-react'

// Loading Spinner
export function Spinner({ size = 'md', className = '' }) {
  const sizes = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6',
    lg: 'h-8 w-8'
  }
  return <Loader2 className={`animate-spin ${sizes[size]} ${className}`} />
}

// Loading overlay per pagine
export function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <Spinner size="lg" className="text-primary-600" />
    </div>
  )
}

// Badge componente
export function Badge({ children, variant = 'default', className = '' }) {
  const variants = {
    default: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    success: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    warning: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    danger: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    error: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    info: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    primary: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
  }

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${variants[variant]} ${className}`}>
      {children}
    </span>
  )
}

// Empty State
export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="text-center py-12">
      {Icon && (
        <div className="mx-auto w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-4">
          <Icon className="h-8 w-8 text-gray-400" />
        </div>
      )}
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
        {title}
      </h3>
      {description && (
        <p className="text-gray-500 dark:text-gray-400 mb-4">
          {description}
        </p>
      )}
      {action}
    </div>
  )
}

// Card componente
export function Card({ children, className = '', padding = true }) {
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 ${padding ? 'p-4 lg:p-6' : ''} ${className}`}>
      {children}
    </div>
  )
}

/**
 * Toggle Switch
 * 
 * Accetta sia 'enabled' che 'checked' per retrocompatibilità
 * @param {boolean} enabled - Stato toggle (alias: checked)
 * @param {boolean} checked - Stato toggle (alias: enabled)
 * @param {function} onChange - Callback con nuovo valore booleano
 * @param {string} label - Label opzionale
 * @param {string} description - Descrizione opzionale
 * @param {boolean} disabled - Disabilita il toggle
 */
export function Toggle({ 
  enabled, 
  checked, 
  onChange, 
  label, 
  description,
  disabled = false 
}) {
  // Supporta sia 'enabled' che 'checked'
  const isOn = enabled ?? checked ?? false
  
  const handleClick = () => {
    if (!disabled && onChange) {
      onChange(!isOn)
    }
  }
  
  return (
    <div className={`flex items-center ${label || description ? 'justify-between' : ''}`}>
      {(label || description) && (
        <div className="flex-1 mr-3">
          {label && (
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {label}
            </p>
          )}
          {description && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {description}
            </p>
          )}
        </div>
      )}
      <button
        type="button"
        role="switch"
        aria-checked={isOn}
        onClick={handleClick}
        disabled={disabled}
        className={`
          relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full
          border-2 border-transparent transition-colors duration-200 ease-in-out
          focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
          ${isOn ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-600'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <span
          className={`
            pointer-events-none inline-block h-5 w-5 transform rounded-full
            bg-white shadow ring-0 transition duration-200 ease-in-out
            ${isOn ? 'translate-x-5' : 'translate-x-0'}
          `}
        />
      </button>
    </div>
  )
}

// Select Input
export function Select({ value, onChange, options, placeholder, className = '' }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`input ${className}`}
    >
      {placeholder && (
        <option value="">{placeholder}</option>
      )}
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  )
}

// Multi-select con checkbox
export function MultiSelect({ values = [], onChange, options, className = '' }) {
  const toggleOption = (optionValue) => {
    if (values.includes(optionValue)) {
      onChange(values.filter(v => v !== optionValue))
    } else {
      onChange([...values, optionValue])
    }
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {options.map((opt) => (
        <label
          key={opt.value}
          className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded-lg -mx-2"
        >
          <input
            type="checkbox"
            checked={values.includes(opt.value)}
            onChange={() => toggleOption(opt.value)}
            className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          />
          <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
            {opt.label}
          </span>
        </label>
      ))}
    </div>
  )
}

// Textarea con auto-resize
export function Textarea({ value, onChange, placeholder, rows = 3, className = '' }) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className={`input resize-none ${className}`}
    />
  )
}

// Confirm Modal
export function ConfirmModal({ isOpen, onClose, onConfirm, title, message, confirmText = 'Conferma', danger = false }) {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6 animate-fade-in">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          {title}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          {message}
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="btn-secondary"
          >
            Annulla
          </button>
          <button
            onClick={() => { onConfirm(); onClose(); }}
            className={danger ? 'btn-primary bg-red-600 hover:bg-red-700' : 'btn-primary'}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  )
}

// Export nuovi componenti PWA
export { Skeleton, SkeletonText, SkeletonCard, SkeletonList, SkeletonTable } from './Skeleton'
export { PullToRefresh } from './PullToRefresh'
export { OnlineIndicator } from './OnlineIndicator'
export { LastUpdated } from './LastUpdated'
export { SaveAnimation, SaveButton } from './SaveAnimation'
export { DuplicateWarning } from './DuplicateWarning'
export { DeleteEntryModal } from './DeleteEntryModal'
